<script setup lang="ts">
    import { Checkbox } from '@/components/ui/checkbox';
    import { CheckboxGroupRoot, useFilter } from 'reka-ui';
    import { Company } from '@/types';
    import { Input } from '@/components/ui/input';
    import { InputRow, Select } from '@/components/ui/form';
    import { Label } from '@/components/ui/label';
    import { optionsYesNo } from '@/utils/form';
    import { ref, computed } from 'vue';
    import { Separator } from '@/components/ui/separator';
    import { useCompanyData } from '@/composables/useCompanyData';
    import Loading from '@/components/Loading.vue';
    import SearchInput from '@/components/SearchInput.vue';

    const { companies, isPlaceholderDataCompanies } = useCompanyData();

    const props = defineProps<{
        form: any;
    }>();

    const searchTerm = ref('');
    const { contains } = useFilter({ sensitivity: 'base' });

    const filteredCompanies = computed(() =>
        companies.value.filter((company: Company) => contains(company.name, searchTerm.value)),
    );

    const allSelected = computed({
        get: () => props.form.role_company_ids.length === companies.value.length,
        set: () => toggleAll(),
    });

    const toggleAll = () => {
        props.form.role_company_ids = allSelected.value
            ? []
            : companies.value.map((company: Company) => company.id);
    };
</script>

<template>
    <div class="grid gap-3 sm:grid-cols-2">
        <InputRow
            :form="form"
            name="name"
            label="roles.table.name"
        >
            <Input
                id="name"
                v-model="form.name"
                autocomplete="name"
                :placeholder="$t('roles.table.name')"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="display_name"
            label="roles.table.display_name"
        >
            <Input
                id="display_name"
                v-model="form.display_name"
                autocomplete="display_name"
                :placeholder="$t('roles.table.display_name')"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="is_client_text"
            label="roles.table.is_client_text"
        >
            <Select
                v-model="form.is_client"
                :options="optionsYesNo"
                :translate="true"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="description"
            label="roles.table.description"
        >
            <Input
                id="description"
                v-model="form.description"
                autocomplete="description"
                :placeholder="$t('roles.table.description')"
            />
        </InputRow>
        <div class="col-span-2 flex flex-col gap-2">
            <Label>{{ $t('roles.select.companies.placeholder') }}</Label>
            <SearchInput
                :placeholder="$t('roles.select.companies.search_options.default')"
                v-model="searchTerm"
                :disabled="isPlaceholderDataCompanies"
            />
        </div>
        <div
            v-if="!isPlaceholderDataCompanies"
            class="col-span-2 flex flex-row items-center gap-2"
        >
            <Label class="flex flex-row items-center gap-2 font-normal">
                <Checkbox v-model="allSelected" />
                <span>{{
                    allSelected
                        ? $t('roles.select.texts.unselectAll')
                        : $t('roles.select.texts.selectAll')
                }}</span>
            </Label>
            <Separator
                orientation="vertical"
                class="h-2.5!"
            />
            <span class="text-xs font-bold">
                {{ form.role_company_ids.length }} {{ $t('roles.select.texts.selectedOptions') }}
            </span>
        </div>
        <CheckboxGroupRoot
            v-if="!isPlaceholderDataCompanies"
            v-model="form.role_company_ids"
            class="col-span-2 -mx-5 grid grid-cols-2 gap-2 px-5 sm:grid-cols-4"
        >
            <Label
                v-for="company in filteredCompanies"
                class="flex flex-row items-center gap-2 font-normal break-all"
            >
                <Checkbox :value="company.id" />
                <span>{{ company.name }}</span>
            </Label>
        </CheckboxGroupRoot>
        <Loading
            v-if="isPlaceholderDataCompanies"
            class="col-span-2"
        />
    </div>
</template>
